import React, { useState } from "react";
import { 
  registerPlayer as apiRegisterPlayer, 
  uploadAttachment as apiUploadAttachment, 
  verifyPaymentStatus as apiVerifyPaymentStatus, 
  initiatePhonePePayment as apiInitiatePhonePePayment 
} from "../api";

const RegistrationSection = () => {
  const [category, setCategory] = useState("");
  const [aadhaar, setAadhaar] = useState(null);
  const [dob, setDob] = useState("");
  const [dobError, setDobError] = useState("");
  const [player1, setPlayer1] = useState("");
  const [player2, setPlayer2] = useState("");
  const [mobile1, setMobile1] = useState("");
  const [mobile2, setMobile2] = useState("");
  const [mobile1Error, setMobile1Error] = useState("");
  const [mobile2Error, setMobile2Error] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [aadhaarError, setAadhaarError] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(""); // '', 'success', 'cancel', 'error'
  const [paymentUrl, setPaymentUrl] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [player1Error, setPlayer1Error] = useState("");
  const [player2Error, setPlayer2Error] = useState("");
  const [aadhaarInputKey, setAadhaarInputKey] = useState(0);
  const [aadhaarPreview, setAadhaarPreview] = useState(null);

  // Use event date for age validation
  // Age limit: 15 years or below on August 15 & 16, 2025
  const eventDates = [new Date(2025, 7, 15), new Date(2025, 7, 16)]; // August 15 & 16, 2025
  // For validation, must be 15 or younger on both dates
  const isUnder15OnBothDates = (dobStr) => {
    if (!dobStr) return true;
    const birthDate = new Date(dobStr);
    for (const eventDate of eventDates) {
      const sixteenthBirthday = new Date(birthDate);
      sixteenthBirthday.setFullYear(sixteenthBirthday.getFullYear() + 16);
      if (sixteenthBirthday <= eventDate) {
        return false;
      }
    }
    return true;
  };

  const minDob = "2010-08-15";
  const PLAYER_NAME_MAX_LENGTH = 64;

  const handleDobChange = (e) => {
    const value = e.target.value;
    setDob(value);
    if (category === "Boys Single" || category === "Girls Single") {
      if (value) {
        if (value < minDob) {
          setDobError("Date of birth cannot be before 15-08-2010.");
          return;
        }
        if (!isUnder15OnBothDates(value)) {
          setDobError("Player must be 15 years old or younger as of August 15 & 16, 2025.");
        } else {
          setDobError("");
        }
      } else {
        setDobError("");
      }
    }
  };

  const validateMobile = (value) => {
    return /^[0-9]{10}$/.test(value);
  };

  // Clear form data
  const clearForm = () => {
    setCategory("");
    setDob("");
    setAadhaar(null);
    setAadhaarPreview(null);
    setPlayer1("");
    setPlayer2("");
    setMobile1("");
    setMobile2("");
    setMobile1Error("");
    setMobile2Error("");
    setDobError("");
    setAadhaarError("");
  };

  // API call for registration
  const registerPlayer = async (data) => {
    return apiRegisterPlayer(data);
  };

  // API call for file upload
  const uploadAttachment = async (shuttleBookingID, file) => {
    return apiUploadAttachment(shuttleBookingID, file);
  };

  // Helper to clear form and close modal
  const clearFormAndCloseModal = () => {
    clearForm();
    setShowPaymentModal(false);
    setOrderId(null);
  };

  // Function to verify payment status from backend
  const verifyPaymentStatus = async (shuttleBookingIDParam) => {
    setPaymentStatus('verifying');
    try {
      const shuttleBookingID = shuttleBookingIDParam ?? orderId; // Prefer parameter, fallback to state
      if (!shuttleBookingID) {
        // Unable to verify payment status: shuttleBookingID is not available
        setPaymentStatus('error');
        return;
      }
      
      const data = await apiVerifyPaymentStatus(shuttleBookingID);

      if (data.state === 'COMPLETED') {
        setPaymentStatus('success');
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 5000);
        clearFormAndCloseModal();
      } else if (data.state === 'FAILED') {
        setPaymentStatus('failed');
        clearFormAndCloseModal();
      } else if (data.state === 'TIMEOUT') {
        setPaymentStatus('timeout');
        clearFormAndCloseModal();
      } else if (data.state === 'PENDING') {
        setPaymentStatus('pending');
        clearFormAndCloseModal();
      } else {
        setPaymentStatus('pending'); // Default to pending for unknown states
        clearFormAndCloseModal();
      }
    } catch (err) {
      setPaymentStatus('error');
      clearFormAndCloseModal();
    }
  };

  // Function to start PhonePe payment in IFRAME
  const startPhonePePayment = (tokenUrl, bookingId) => {
    if (window.PhonePeCheckout && window.PhonePeCheckout.transact) {
      window.PhonePeCheckout.transact({
        tokenUrl,
        type: "IFRAME",
        callback: (response) => {
          if (response === 'USER_CANCEL') {
            setPaymentStatus('cancel');
            setShowPaymentModal(false);
          } else {
            setShowPaymentModal(false);
            verifyPaymentStatus(bookingId);
          }
        }
      });
    } else {
      setPaymentStatus('error');
      setShowPaymentModal(false);
      setError("Payment system unavailable. Please try again later.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset all errors
    setError("");
    setMobile1Error("");
    setMobile2Error("");
    setDobError("");
    setAadhaarError("");
    setPlayer1Error("");
    setPlayer2Error("");
    let hasError = false;

    // Validate if category is selected
    if (!category) {
      setError("Please select a category first");
      hasError = true;
    }

    // Validate required fields based on category
    if (category === "Boys Single" || category === "Girls Single") {
      if (!player1.trim()) {
        setPlayer1Error("Please enter player name");
        hasError = true;
      }
      if (!mobile1) {
        setMobile1Error("Mobile number is required");
        hasError = true;
      }
      if (!dob) {
        setDobError("Date of birth is required");
        hasError = true;
      }
      if (!aadhaar) {
        setAadhaarError("Aadhaar upload is required for singles");
        hasError = true;
      }
    }

    if (category === "Doubles (men or mixed)") {
      if (!player1.trim()) {
        setPlayer1Error("Please enter player 1 name");
        hasError = true;
      }
      if (!player2.trim()) {
        setPlayer2Error("Please enter player 2 name");
        hasError = true;
      }
      if (!mobile1) {
        setMobile1Error("Mobile number 1 is required");
        hasError = true;
      }
      if (!mobile2) {
        setMobile2Error("Mobile number 2 is required");
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }
    
    setLoading(true);
    setSuccess("");
    setError("");
    setAadhaarError("");
    
    if ((category === "Boys Single" || category === "Girls Single") && dobError) {
      setLoading(false);
      return;
    }
    
    if ((category === "Boys Single" || category === "Girls Single")) {
      if (!validateMobile(mobile1)) {
        setMobile1Error("Enter a valid 10-digit mobile number.");
        setLoading(false);
        return;
      } else {
        setMobile1Error("");
      }
      if (!aadhaar) {
        setAadhaarError("Aadhaar upload is required for singles.");
        setLoading(false);
        return;
      } else {
        setAadhaarError("");
      }
    }
    
    if (category === "Doubles (men or mixed)") {
      if (!validateMobile(mobile1)) {
        setMobile1Error("Enter a valid 10-digit mobile number.");
        setLoading(false);
        return;
      } else {
        setMobile1Error("");
      }
      if (!validateMobile(mobile2)) {
        setMobile2Error("Enter a valid 10-digit mobile number.");
        setLoading(false);
        return;
      } else {
        setMobile2Error("");
      }
    }
    
    try {
      const data = {
        id: 0,
        clientID: 0,
        category: category,
        name1: player1,
        mobileNumber1: mobile1,
        name2: player2 || "",
        mobileNumber2: mobile2 || "",
        dob: dob ? new Date(dob).toISOString() : "2025-07-11T11:03:24.783Z",
        amount: category === "Boys Single" || category === "Girls Single" ? 500 : 600,
        lastActionDate: new Date().toISOString(),
        lastActionUserID: 0,
      };
      
      const shuttleBookingID = await registerPlayer(data);
      
      // Upload Aadhaar if provided
      if (aadhaar && shuttleBookingID) {
        await uploadAttachment(shuttleBookingID, aadhaar);
      }
      
      // Store the order ID for payment verification
      setOrderId(shuttleBookingID);
      
      // Initiate PhonePe payment via backend API
      try {
        const paymentData = await apiInitiatePhonePePayment(shuttleBookingID);
        
        if (paymentData.redirectUrl) {
          setPaymentUrl(paymentData.redirectUrl);
          setShowPaymentModal(true);
          setPaymentStatus('');
          
          // Start PhonePe payment after a short delay to ensure modal is shown
          setTimeout(() => {
            startPhonePePayment(paymentData.redirectUrl, shuttleBookingID);
          }, 100);
        } else {
          throw new Error("No redirect URL received from payment API");
        }
      } catch (paymentError) {
        setPaymentStatus('error');
        setError(`Payment initiation failed: ${paymentError.message}`);
      }
      
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setOrderId(null); // Clear order ID on registration failure
    } finally {
      setLoading(false);
    }
  };

  // Close payment status modals
  const closePaymentModal = () => {
    setPaymentStatus('');
    clearFormAndCloseModal();
  };

  return (
    <section className="py-16 bg-pink-50">
      {/* Snackbar */}
      {showSnackbar && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "40px",
            transform: "translateX(-50%)",
            background: "#16a34a",
            color: "white",
            padding: "16px 32px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontWeight: 600,
            fontSize: "1.1rem",
            letterSpacing: "0.5px",
            minWidth: "220px",
            textAlign: "center",
          }}
        >
          Registration and Payment successful!
        </div>
      )}

      {/* Payment Result Modals */}
      {paymentStatus === 'verifying' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-blue-600 text-xl font-bold">Verifying Payment...</h2>
            <p className="mb-4">Please wait while we confirm your payment status.</p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      )}
      
      {paymentStatus === 'success' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-green-600 text-xl font-bold">Payment Successful!</h2>
            <p className="mb-4">Your registration and payment are complete.</p>
            <button 
              className="bg-green-500 text-white px-4 py-2 rounded w-full" 
              onClick={closePaymentModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {paymentStatus === 'failed' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-red-600 text-xl font-bold">Payment Failed</h2>
            <p className="mb-4">Your payment was not successful. Please try again or use a different payment method.</p>
            <button 
              className="bg-red-500 text-white px-4 py-2 rounded w-full" 
              onClick={closePaymentModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {paymentStatus === 'timeout' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-orange-600 text-xl font-bold">Payment Timed Out</h2>
            <p className="mb-4">Payment timed out, please try again.</p>
            <button 
              className="bg-orange-500 text-white px-4 py-2 rounded w-full" 
              onClick={closePaymentModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {paymentStatus === 'pending' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-yellow-600 text-xl font-bold">Payment Pending</h2>
            <p className="mb-4">Your payment is still being processed. Please check again later or contact support if you do not receive confirmation.</p>
            <button 
              className="bg-yellow-500 text-white px-4 py-2 rounded w-full" 
              onClick={closePaymentModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {paymentStatus === 'cancel' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="mb-2 text-yellow-600 text-xl font-bold">Payment Cancelled</h2>
            <p className="mb-4">You cancelled the payment. Please try again if you wish to complete registration.</p>
            <button 
              className="bg-yellow-500 text-white px-4 py-2 rounded w-full" 
              onClick={closePaymentModal}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Full-screen overlay spinner when loading */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-red-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span className="text-black font-semibold text-lg">Processing...</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-base text-red-600 font-bold uppercase tracking-wide">Your Game Starts Here</span>
          <h2 className="text-4xl font-extrabold mb-4 mt-2 text-gray-800">Join The Clash – Register Now</h2>
          <p className="text-lg text-gray-700 mb-4">
            <span className="font-bold">Ready to take the court?</span> Fill out the form to secure your spot. <span className="font-bold">Limited slots available — don't miss out!</span>
          </p>
          <ul className="mb-4 text-gray-700 text-base list-disc pl-5">
            {/* <li><span className="font-semibold">Boys Single & Girls Single:</span> Age limit: <span className="text-red-600 font-bold">15 years or below on August 15 & 16, 2025</span></li> */}
            <li><span className="font-semibold">Doubles (men or mixed):</span> <span className="text-green-700 font-bold">No age limit</span></li>
          </ul>
          {/* Amount Cards - Always side by side */}
          <div className="mt-8 flex flex-row gap-6 w-full justify-center">
            {/* <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-100 transform hover:scale-105 transition-transform duration-300 flex-1 min-w-0 max-w-xs">
              <p className="text-xl font-bold text-gray-700">Singles</p>
              <p className="text-4xl font-extrabold text-red-600 mt-2">₹500</p>
              <p className="text-sm text-gray-500 mt-1">per player</p>
            </div> */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-100 transform hover:scale-105 transition-transform duration-300 flex-1 min-w-0 max-w-xs">
              <p className="text-xl font-bold text-gray-700">Doubles</p>
              <p className="text-4xl font-extrabold text-red-600 mt-2">₹600</p>
              <p className="text-sm text-gray-500 mt-1">per team</p>
            </div>
          </div>
        </div>
        
        <div 
          id="register-form"
          className="bg-white rounded-2xl shadow-lg p-4 md:p-8 flex flex-col gap-6 w-full max-w-md mx-auto"
        >
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">Registration Form</h3>
          {success && <div className="text-green-600 font-semibold text-center">{success}</div>}
          {error && error !== "Please enter player name" && error !== "Please enter player 1 name" && error !== "Please enter player 2 name" && (
            <div className="text-red-600 font-semibold text-center">{error}</div>
          )}
          
          <div className="relative w-full">
            <label className="block text-gray-700 mb-1 font-medium">Category <span className="text-red-500">*</span></label>
            <select
              className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full appearance-none pr-12"
              value={category}
              onChange={e => {
                setCategory(e.target.value);
                setDob("");
                setDobError("");
                setAadhaar(null);
                setAadhaarPreview(null);
                setAadhaarInputKey(prev => prev + 1);
                setPlayer1("");
                setPlayer2("");
                setMobile1("");
                setMobile2("");
                setMobile1Error("");
                setMobile2Error("");
                setError(""); // Clear any previous errors
                setAadhaarError("");
                setPlayer1Error("");
                setPlayer2Error("");
              }}
              required
            >
              <option value="">Select Category</option>
              {/* <option>Boys Single</option> */}
              {/* <option>Girls Single</option> */}
              <option>Doubles (men or mixed)</option>
            </select>
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>

          {/* Singles Fields */}
          {(category === "Boys Single" || category === "Girls Single") && (
            <>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Player Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                  placeholder="Player Name *"
                className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                value={player1}
                  maxLength={PLAYER_NAME_MAX_LENGTH + 1}
                  onChange={e => {
                    const val = e.target.value;
                    setPlayer1(val);
                    if (val.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer1Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (val.trim() === "") {
                      setPlayer1Error("Please enter player name");
                    } else if (player1Error) {
                      setPlayer1Error("");
                    }
                  }}
                  onBlur={e => {
                    if (e.target.value.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer1Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (e.target.value.trim() === "") {
                      setPlayer1Error("Please enter player name");
                    }
                  }}
                required
              />
                {player1Error && (
                  <span className="text-xs text-red-500 block mt-1">{player1Error}</span>
                )}
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  placeholder="Mobile Number *"
                  className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                  value={mobile1}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setMobile1(val);
                    if (val === "") {
                      setMobile1Error("Mobile number is required");
                    } else if (mobile1Error) {
                      setMobile1Error("");
                    }
                  }}
                  required
                />
                {mobile1Error && <span className="text-xs text-red-500 block mt-1">{mobile1Error}</span>}
              </div>
              
              <div className="relative w-full">
                <label className="block text-gray-700 mb-1 font-medium">Date of Birth <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  placeholder="Date of Birth *"
                  className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 w-full focus:ring-2 focus:ring-red-400 pr-12"
                  value={dob}
                  onChange={e => {
                    handleDobChange(e);
                    const val = e.target.value;
                    if (val === "") {
                      setDobError("Date of birth is required");
                    } else if ((category === "Boys Single" || category === "Girls Single") && val.length === 10) {
                      // Only check if full date is typed
                      if (val < minDob) {
                        setDobError("Date of birth cannot be before 15-08-2010.");
                      } else {
                        setDobError("");
                      }
                    } else if (dobError) {
                      setDobError("");
                    }
                  }}
                  onInput={e => {
                    // Restrict year to 4 digits
                    const val = e.target.value;
                    const parts = val.split('-');
                    if (parts[0] && parts[0].length > 4) {
                      parts[0] = parts[0].slice(0, 4);
                      e.target.value = parts.join('-');
                    }
                    // Custom min date validation while typing
                    if ((category === "Boys Single" || category === "Girls Single") && val && val < minDob) {
                      setDobError("Date of birth cannot be before 15-08-2010.");
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if ((category === "Boys Single" || category === "Girls Single") && val && val < minDob) {
                      setDobError("Date of birth cannot be before 15-08-2010.");
                    }
                  }}
                  min={category === "Boys Single" || category === "Girls Single" ? "2010-08-15" : undefined}
                  required
                />
                <span className="absolute right-4 inset-y-5 mt-8 flex items-center text-gray-400 pointer-events-none md:hidden">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                {dobError && <span className="text-xs text-red-500 block mt-1">{dobError}</span>}
              </div>
              
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Aadhaar Upload <span className="text-red-500">*</span></label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.docx"
                  className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                  key={aadhaarInputKey}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) {
                      setAadhaar(null);
                      setAadhaarPreview(null);
                      setAadhaarError("Aadhaar upload is required for singles");
                      return;
                    }
                    const allowedTypes = [
                      'application/pdf',
                      'image/jpeg',
                      'image/png',
                      'image/webp',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ];
                    if (!allowedTypes.includes(file.type)) {
                      setAadhaar(null);
                      setAadhaarPreview(null);
                      setAadhaarError("Only PDF, JPG, PNG, WEBP, or DOCX files are allowed.");
                      return;
                    }
                    if (file.size > 10 * 1024 * 1024) {
                      setAadhaar(null);
                      setAadhaarPreview(null);
                      setAadhaarError("File size must be 10MB or less.");
                      return;
                    }
                    setAadhaar(file);
                    setAadhaarError("");
                    // Preview for images
                    if (file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = ev => setAadhaarPreview(ev.target.result);
                      reader.readAsDataURL(file);
                    } else {
                      setAadhaarPreview(null);
                    }
                  }}
                />
                {/* Preview and remove button */}
                {aadhaar && (
                  <div className="flex items-center mt-2 gap-2">
                    {/* Image preview */}
                    {aadhaarPreview && (
                      <img src={aadhaarPreview} alt="Preview" className="h-12 w-12 object-cover rounded border" />
                    )}
                    {/* PDF/DOCX icon and filename */}
                    {!aadhaarPreview && (
                      <span className="flex items-center gap-1 text-gray-700">
                        {aadhaar.type === 'application/pdf' && (
                          <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        )}
                        {aadhaar.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' && (
                          <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v16H4z" /></svg>
                        )}
                        <span className="truncate max-w-xs">{aadhaar.name}</span>
                      </span>
                    )}
                    {/* Remove button */}
                    <button type="button" className="ml-2 text-red-500 hover:text-red-700 text-lg font-bold" onClick={() => { setAadhaar(null); setAadhaarPreview(null); setAadhaarInputKey(prev => prev + 1); }} aria-label="Remove file">×</button>
                  </div>
                )}
                {aadhaarError && <span className="text-xs text-red-500 block mt-1">{aadhaarError}</span>}
              </div>
              <div className="text-lg font-bold text-gray-700">Amount: <span className="text-red-600">₹500</span></div>
            </>
          )}

          {/* Doubles Fields */}
          {category === "Doubles (men or mixed)" && (
            <>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Player Name 1 <span className="text-red-500">*</span></label>
              <input
                type="text"
                  placeholder="Player Name 1 *"
                className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                value={player1}
                  maxLength={PLAYER_NAME_MAX_LENGTH + 1}
                  onChange={e => {
                    const val = e.target.value;
                    setPlayer1(val);
                    if (val.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer1Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (val.trim() === "") {
                      setPlayer1Error("Please enter player 1 name");
                    } else if (player1Error) {
                      setPlayer1Error("");
                    }
                  }}
                  onBlur={e => {
                    if (e.target.value.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer1Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (e.target.value.trim() === "") {
                      setPlayer1Error("Please enter player 1 name");
                    }
                  }}
                required
              />
                {player1Error && (
                  <span className="text-xs text-red-500 block mt-1">{player1Error}</span>
                )}
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Player Name 2 <span className="text-red-500">*</span></label>
              <input
                type="text"
                  placeholder="Player Name 2 *"
                className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                value={player2}
                  maxLength={PLAYER_NAME_MAX_LENGTH + 1}
                  onChange={e => {
                    const val = e.target.value;
                    setPlayer2(val);
                    if (val.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer2Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (val.trim() === "") {
                      setPlayer2Error("Please enter player 2 name");
                    } else if (player2Error) {
                      setPlayer2Error("");
                    }
                  }}
                  onBlur={e => {
                    if (e.target.value.length > PLAYER_NAME_MAX_LENGTH) {
                      setPlayer2Error(`Player name cannot exceed ${PLAYER_NAME_MAX_LENGTH} characters.`);
                    } else if (e.target.value.trim() === "") {
                      setPlayer2Error("Please enter player 2 name");
                    }
                  }}
                required
              />
                {player2Error && (
                  <span className="text-xs text-red-500 block mt-1">{player2Error}</span>
                )}
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Mobile Number 1 <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  placeholder="Mobile Number 1 *"
                  className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                  value={mobile1}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setMobile1(val);
                    if (val === "") {
                      setMobile1Error("Mobile number 1 is required");
                    } else if (mobile1Error) {
                      setMobile1Error("");
                    }
                  }}
                  required
                />
                {mobile1Error && <span className="text-xs text-red-500 block mt-1">{mobile1Error}</span>}
              </div>
              <div>
                <label className="block text-gray-700 mb-1 font-medium">Mobile Number 2 <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  placeholder="Mobile Number 2 *"
                  className="bg-gray-50 border-none rounded-lg px-4 py-3 text-lg text-gray-700 focus:ring-2 focus:ring-red-400 w-full"
                  value={mobile2}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setMobile2(val);
                    if (val === "") {
                      setMobile2Error("Mobile number 2 is required");
                    } else if (mobile2Error) {
                      setMobile2Error("");
                    }
                  }}
                  required
                />
                {mobile2Error && <span className="text-xs text-red-500 block mt-1">{mobile2Error}</span>}
              </div>
              <div className="text-lg font-bold text-gray-700">Amount: <span className="text-red-600">₹600</span></div>
            </>
          )}

          <button 
            onClick={handleSubmit}
            className={`bg-red-500 text-white rounded-lg px-4 py-3 font-semibold text-lg transition w-full ${
              loading || !category || ((category === "Boys Single" || category === "Girls Single") && dobError) || mobile1Error || mobile2Error
                ? 'opacity-50 cursor-not-allowed bg-gray-400' 
                : 'hover:bg-red-600'
            }`}
            disabled={loading || !category || ((category === "Boys Single" || category === "Girls Single") && dobError) || mobile1Error || mobile2Error}
          >
            {"Let's Play"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default RegistrationSection;